import {
  IPaginate,
  IProduct,
  ISettlementReportGraphData,
  ISettlementReports,
  RootState,
  StoreState,
} from '@/types/types';
import { ActionTree, GetterTree, Module, ModuleTree, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { UssdCodesService } from '@/services/ussd-codes.service';
import { SettlementReportsService } from '@/services/settlement-reports.service';
import qs from 'qs';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, exhaustMap, switchMap } from 'rxjs/operators';
import { organizations } from '@/store/modules/organizations';

const state: StoreState<ISettlementReports> = {
  all: [],
  userGeneratedList: [],
  totalSettlementAmount: 0,
  settlementReportGraphData: [],
  details: {
    _id: '',
    date: '',
    totalAmount: 0,
    settlementStatus: '',
    organisationName: '',
    organisationId: '',
    createdAt: '',
    service: '',
    reportType: '',
    dateRange: '',
  },
  generatedReportDates: [],
  paginate: {
    total: 0,
    itemsPerPage: 15,
    page: 1,
    pages: 0,
  },
  reset: {
    id: '',
    code: '',
    apiUrl: '',
    description: '',
    consumer: '',
    status: '',
  },
};

const mutations: MutationTree<StoreState<ISettlementReports>> = {
  UPDATE_ALL_SETTLEMENT_REPORTS(state, payload: Array<ISettlementReports>) {
    state.all = payload;
  },
  UPDATE_TOTAL_SETTLEMENT_AMOUNT(state, payload: number) {
    state.totalSettlementAmount = payload;
  },
  UPDATE_SETTLEMENT_REPORT_GRAPH_DATA(state, payload: Array<ISettlementReportGraphData>) {
    state.settlementReportGraphData = payload;
  },
  UPDATE_PAGINATION(state, payload: IPaginate) {
    state.paginate = payload;
  },
  UPDATE_USER_GENERATED_SETTLEMENT_REPORTS(state, payload: Array<ISettlementReports>) {
    state.userGeneratedList = payload;
  },
  UPDATE_GENERATED_REPORT_DATES(state, payload: Array<string>) {
    state.generatedReportDates = payload;
  },
  UPDATE_SETTLEMENT_REPORT_DETAILS(state, payload: ISettlementReports) {
    state.details = payload;
  },
};

const actions: ActionTree<StoreState<ISettlementReports>, RootState> = {
  async list({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const qParams = qs.parse(query);
      const response$ = await SettlementReportsService.list(query, qParams?.download ? 'blob' : 'json');
      if (response$) {
        dispatch('isPageLoading', false, { root: true });
        if (qParams?.download) {
          const { ext } = qParams;
          const blob = new Blob([response$.data], {
              type: 'application/octet-stream',
            }),
            a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `settlement-reports-${Date.now()}.${ext}`;
          a.click();
          URL.revokeObjectURL(a.href);
        } else {
          const { paginateObj, others } = response$.data.data;
          const { docs, page, pages, total, limit } = paginateObj;
          commit('UPDATE_ALL_SETTLEMENT_REPORTS', docs);
          commit('UPDATE_PAGINATION', {
            page,
            pages,
            total,
            itemsPerPage: limit,
          });
          commit('UPDATE_TOTAL_SETTLEMENT_AMOUNT', others?.totalAmount || 0);
          commit('UPDATE_SETTLEMENT_REPORT_GRAPH_DATA', others?.settlementReportsGraphDataByDate || []);
        }
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async fetchAllGeneratedReportDates({ dispatch, commit }) {
    try {
      const response$ = await SettlementReportsService.fetchAllGeneratedReportDates();
      if (response$) {
        commit('UPDATE_GENERATED_REPORT_DATES', response$.data?.data);
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
    }
  },
  async generateReport({ dispatch, commit }, payload: any) {
    try {
      const { loading, query, body } = payload;
      dispatch('isLoading', true, { root: true });
      const qParams = qs.stringify(query, { skipNulls: true, addQueryPrefix: true });
      const response$ = await SettlementReportsService.generateReport(body, qParams);
      if (response$) {
        dispatch('isLoading', false, { root: true });
        if (body?.requestType === 'PERSIST_DATA') {
          dispatch('snackBarMessage', 'Settlement report saved successfully', {
            root: true,
          });
          dispatch('updateDialog', loading, { root: true });
          dispatch('list', `?page=1&size=15&requestType=USER_GENERATED_REPORT`);
          dispatch('fetchAllGeneratedReportDates');
        } else {
          dispatch('updateDialog', loading, { root: true });
          dispatch('snackBarMessage', 'Settlement report generated successfully', {
            root: true,
          });
          dispatch('updateDialog', { idx: 'preview', state: true }, { root: true });
        }
        dispatch('snackBarVisibility', true, { root: true });
        commit('UPDATE_USER_GENERATED_SETTLEMENT_REPORTS', response$.data?.data);
        dispatch('isDialogLoading', false, { root: true });
      }
    } catch (e) {
      dispatch('isLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
    }
  },
  async updateSettlementReportStatus({ dispatch, commit }, payload: any) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await SettlementReportsService.updateSettlementReportStatus(
        payload?.organizationId,
        payload?.id,
        payload.body,
      );
      if (response$) {
        dispatch('isLoading', false, { root: true });
        dispatch('snackBarMessage', 'Settlement report status updated successfully', {
          root: true,
        });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('list', payload.query);
        //dispatch('fetchAllGeneratedReportDates');
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async uploadPaymentReceipt({ dispatch, commit }, payload: any) {
    try {
      const { organizationId, id, body, loading } = payload;
      dispatch('isLoading', true, { root: true });
      const response$ = await SettlementReportsService.uploadReceipt(id, `?organizationId=${organizationId}`, body);
      if (response$.data?.data) {
        dispatch('isLoading', false, { root: true });
        dispatch('updateDialog', loading, { root: true });
        dispatch('snackBarMessage', 'Settlement report payment receipt uploaded successfully', {
          root: true,
        });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('list', `?page=1&size=15&requestType=USER_GENERATED_REPORT`);
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async fetchGeneratedReportedDetails({ commit, state }, id: string) {
    commit(
      'UPDATE_SETTLEMENT_REPORT_DETAILS',
      state.all.find(item => item._id === id),
    );
  },
};

const getters: GetterTree<StoreState<ISettlementReports>, RootState> = {
  getAllSettlementReports: state => state.all,
  getPagination: state => state.paginate,
  getTotalSettlementAmount: state => state.totalSettlementAmount,
  getSettlementReportGraphData: state => state.settlementReportGraphData,
  getGeneratedSettlementReportDates: state => state.generatedReportDates,
  getUserGeneratedSettlementReportList: state => state.userGeneratedList,
  getGeneratedSettlementReportDetails: state => state.details,
  getResetFormValues: state => state.reset,
};

export const settlementReports: Module<StoreState<ISettlementReports>, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
